import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { LuX } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";

export default function ProjectIconCroppingModal({
  originalImage,
  originalFile,
  onConfirm,
  onCancel,
}: {
  originalImage: string;
  originalFile?: File;
  onConfirm: (croppedImage: File) => void;
  onCancel: () => void;
}) {
  const isCompactView = useIsCompactView();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Small delay to ensure component is mounted before starting animation
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  const handleCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Helper to convert degrees to radians
  function degToRad(degrees: number) {
    return (degrees * Math.PI) / 180;
  }

  // Helper to create an image
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => {
        console.error("Image loading error:", error);
        reject(new Error("Failed to load image"));
      });
      image.src = url;
    });

  // Helper to create a File from canvas
  const createFileFromCanvas = (
    canvas: HTMLCanvasElement,
    originalFile?: File
  ): Promise<File> => {
    const getFileName = () => {
      if (originalFile) {
        const nameWithoutExt = originalFile.name.replace(/\.[^/.]+$/, "");
        const extension =
          originalFile.name.split(".").pop()?.toLowerCase() || "png";
        return `${nameWithoutExt}.${extension}`;
      }
      return "icon.png";
    };

    const getMimeType = () => {
      if (originalFile) {
        return originalFile.type || "image/png";
      }
      return "image/png";
    };

    return new Promise<File>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], getFileName(), { type: getMimeType() }));
        } else {
          reject(new Error("Failed to create blob"));
        }
      }, getMimeType());
    });
  };

  // Helper to get the cropped image with rotation
  async function getCroppedImg(
    imageSrc: string,
    pixelCrop: any,
    rotation = 0,
    originalFile?: File
  ): Promise<File> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No 2d context");

    const rotRad = degToRad(rotation);

    // Calculate bounding box of the rotated image
    const bBoxWidth =
      Math.abs(Math.cos(rotRad) * image.width) +
      Math.abs(Math.sin(rotRad) * image.height);
    const bBoxHeight =
      Math.abs(Math.sin(rotRad) * image.width) +
      Math.abs(Math.cos(rotRad) * image.height);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Move the origin to the center of the crop area
    ctx.translate(-pixelCrop.x, -pixelCrop.y);
    // Move to center of original image
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    // Rotate
    ctx.rotate(rotRad);
    // Move image back by half its size
    ctx.translate(-image.width / 2, -image.height / 2);
    // Draw the image
    ctx.drawImage(image, 0, 0);

    // Resize to 1024x1024 if needed
    if (canvas.width !== 1024 || canvas.height !== 1024) {
      const resizeCanvas = document.createElement("canvas");
      resizeCanvas.width = 1024;
      resizeCanvas.height = 1024;
      const resizeCtx = resizeCanvas.getContext("2d");
      if (!resizeCtx) throw new Error("No 2d context");
      resizeCtx.drawImage(canvas, 0, 0, 1024, 1024);
      return createFileFromCanvas(resizeCanvas, originalFile);
    }

    return createFileFromCanvas(canvas, originalFile);
  }

  const handleConfirm = async () => {
    if (originalImage && croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(
          originalImage,
          croppedAreaPixels,
          rotation,
          originalFile
        );
        setIsVisible(false);
        setTimeout(() => {
          onConfirm(croppedImage);
        }, 200);
      } catch (error) {
        console.error("Error cropping image:", error);
        // You could add a toast notification here for error handling
      }
    } else {
      console.error("Missing originalImage or croppedAreaPixels");
    }
  };

  const handleCancel = () => {
    setIsVisible(false);
    // Wait for fade out animation to complete before calling onCancel
    setTimeout(() => {
      onCancel();
    }, 200);
  };

  return (
    <div
      className={`${
        isVisible ? "opacity-100" : "opacity-0"
      } fixed inset-0 bg-black/50 backdrop-blur-sm 
        flex items-center justify-center z-50
        text-light-text dark:text-dark-text
        transition-opacity duration-200`}
    >
      <div
        className={`${isCompactView ? "h-full" : "rounded-lg max-w-2xl mx-4"}
          flex flex-col gap-5
          bg-light-surface-secondary dark:bg-dark-surface-secondary 
          p-6 w-full`}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Crop Image</h3>
          <button
            onClick={handleCancel}
            className="text-light-placeholder dark:text-dark-placeholder
            hover:brightness-150 transition-all duration-300"
          >
            <LuX size={24} />
          </button>
        </div>

        <div className="relative h-96">
          <Cropper
            image={originalImage}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
            style={{
              containerStyle: {
                width: "100%",
                height: "100%",
                backgroundColor: "#f0f0f0",
              },
            }}
          />
        </div>

        <div
          className={`${
            isCompactView && "flex-col gap-8"
          } flex justify-between items-center`}
        >
          <div
            className={`${
              isCompactView && "justify-center flex-col"
            } flex items-start gap-6 w-full`}
          >
            <div className="w-full flex flex-col items-start gap-2">
              <label className="">Zoom:</label>
              <div className="w-full flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full h-5 rounded-xl appearance-none cursor-pointer 
                bg-white/50 dark:bg-black/50 
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-blue [&::-webkit-slider-thumb]:cursor-pointer 
                [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 
                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-blue [&::-moz-range-thumb]:cursor-pointer 
                [&::-moz-range-thumb]:border-0"
                />
                <span className="w-[50px]">{Math.round(zoom * 100)}%</span>
              </div>
            </div>

            <div className="w-full flex flex-col items-start gap-2">
              <label className="">Rotate:</label>
              <div className="w-full flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={360}
                  step={1}
                  value={rotation}
                  onChange={(e) => setRotation(Number(e.target.value))}
                  className="w-full h-5 rounded-xl appearance-none cursor-pointer 
                bg-white/50 dark:bg-black/50 
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-blue [&::-webkit-slider-thumb]:cursor-pointer 
                [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 
                [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-blue [&::-moz-range-thumb]:cursor-pointer 
                [&::-moz-range-thumb]:border-0"
                />
                <span className="w-[50px]">{rotation}°</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 
              hover:opacity-50 dark:hover:brightness-150 
              transition-all duration-300"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-primary-blue text-white rounded 
              hover:brightness-125 transition-all duration-300"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
